/**
 * Modal dialog.
 *
 * Replaces the inline panels these screens used to grow. An expanding panel pushes the
 * table down the page, so the thing you were looking at when you clicked moves, and on
 * a long list the form opens below the fold and nothing appears to happen at all.
 *
 * Everything here is the boring part of a dialog that is easy to leave out and painful
 * to be without: focus goes in and is trapped, Escape closes, the page behind does not
 * scroll, and focus returns to whatever opened it.
 */

import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useI18n } from '@/i18n';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const WIDTH: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: ModalSize;
  /** Actions. Pinned below the scrolling body so a long form's Save is always reachable. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  // Remember the trigger before the dialog steals focus, so closing puts the caret back
  // on the button that opened it rather than at the top of the document.
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;

    return () => {
      restoreFocusTo.current?.focus?.();
    };
  }, [open]);

  // Move focus into the dialog, preferring the first *field* over the first focusable.
  // In DOM order the first focusable is the close button in the header, and opening a
  // form with the caret on "dismiss" is the wrong place to start.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const field = panel.querySelector<HTMLElement>(
      'input:not([disabled]):not([type="checkbox"]), select:not([disabled]), textarea:not([disabled])',
    );
    // Fall back to the panel itself rather than to nothing, so a screen reader announces
    // the title instead of leaving the user outside the dialog.
    (field ?? panel).focus({ preventScroll: true });
  }, [open]);

  // The page behind must not scroll. Padding compensates for the vanished scrollbar so
  // the layout does not jump sideways as the dialog opens.
  useEffect(() => {
    if (!open) return;

    const { overflow, paddingRight } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [open]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      // Focus trap. Without it, tabbing walks into the page behind the dialog, which is
      // invisible and inert to a mouse but perfectly reachable from a keyboard.
      const panel = panelRef.current;
      if (!panel) return;

      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) return;

      const first = items[0]!;
      const last = items[items.length - 1]!;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-2 sm:p-6"
      role="presentation"
      onKeyDown={onKeyDown}
    >
      {/* Backdrop. A separate element rather than a border on the panel so the click
          target is unambiguous — mousedown on it, not a drag that ended on it. */}
      <div
        className="fixed inset-0 bg-[rgba(12,17,29,0.55)] backdrop-blur-[2px] animate-[fadeIn_120ms_ease-out]"
        aria-hidden="true"
        onMouseDown={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        /* `dvh`, not `vh`: on a phone `100vh` is the height with the browser chrome
           hidden, so a dialog sized against it runs its footer underneath the address
           bar and the Save button cannot be reached. */
        className={`card relative w-full ${WIDTH[size]} my-2 sm:my-8 shadow-[var(--shadow-md)] outline-none animate-[modalIn_140ms_ease-out] flex flex-col max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-4rem)]`}
      >
        <div className="flex items-start gap-4 px-4 sm:px-5 py-4 border-b border-border shrink-0">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="font-bold text-lg leading-tight">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="text-sm text-muted mt-1 leading-relaxed">
                {description}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="shrink-0 -me-1 -mt-1 w-8 h-8 grid place-items-center rounded text-subtle hover:text-text hover:bg-surface-alt transition-colors"
          >
            <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="m6 6 8 8M14 6l-8 8" />
            </svg>
          </button>
        </div>

        <div className="px-4 sm:px-5 py-5 overflow-y-auto">{children}</div>

        {footer ? (
          /* Wraps on a narrow screen rather than letting a third button fall off
             the right edge where nothing suggests it is there. */
          <div className="px-4 sm:px-5 py-4 border-t border-border flex flex-wrap items-center gap-2 shrink-0">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
